import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IPhoneTapReminder } from '@/components/mobile/IPhoneTapReminder';
import { useEnhancedWakeLock } from '@/hooks/voice/useEnhancedWakeLock';
import { useIPhoneTapReminder } from '@/hooks/voice/useIPhoneTapReminder';
import { useGenieSetup } from './hooks/useGenieSetup';
import { useResumeSetup } from './hooks/useResumeSetup';
import { useTemplatePreload } from './hooks/useTemplatePreload';
import { TalkSetupView } from './TalkSetupView';
import { ConfirmationView } from './ConfirmationView';
import { ListenerSetupForm } from './components/ListenerSetupForm';

const GenieSetupPage = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");

  const genieSetup = useGenieSetup();
  const { setupPhase, connected, dispatch } = genieSetup;

  const wakeLock = useEnhancedWakeLock();
  const tapReminder = useIPhoneTapReminder({
    onTap: () => wakeLock.reactivateAll(),
  });

  useTemplatePreload(templateId, setupPhase, connected, dispatch);
  useResumeSetup(setupPhase, connected, dispatch);

  const handleConversationToggleWithWakeLock = async () => {
    const wasConnected = connected;
    await genieSetup.handleConversationToggle();

    if (!wasConnected && connected) {
      await wakeLock.requestWakeLock();
      tapReminder.startReminder();
    } else if (wasConnected && !connected) {
      await wakeLock.releaseWakeLock();
      tapReminder.stopReminder();
    }
  };

  useEffect(() => {
    return () => {
      wakeLock.releaseWakeLock();
      tapReminder.stopReminder();
    };
  }, [wakeLock, tapReminder]);

  const isConfirmationPhase = setupPhase === "confirmation" || setupPhase === "complete";
  const isListenerSetupPhase = setupPhase === "listener-setup";

  return (
    <div className="h-screen w-full bg-[#F9FAFC] overflow-auto flex flex-col md:flex-row relative">
      {isListenerSetupPhase ? (
        <ListenerSetupForm
          onConfirm={genieSetup.createGenieFromAnalysis}
          onBack={() => genieSetup.setSetupPhase("conversation")}
          isCreating={genieSetup.isCreatingGenie}
        />
      ) : isConfirmationPhase ? (
        <ConfirmationView
          setupPhase={setupPhase}
          analysisResult={genieSetup.analysisResult}
          isCreatingGenie={genieSetup.isCreatingGenie}
          createdAgent={genieSetup.createdAgent}
          createGenieFromAnalysis={genieSetup.createGenieFromAnalysis}
          resetSetup={genieSetup.resetSetup}
        />
      ) : (
        <TalkSetupView
          speaking={genieSetup.speaking}
          listening={genieSetup.listening}
          thinking={genieSetup.thinking}
          connected={genieSetup.connected}
          isConnecting={genieSetup.isConnecting}
          setupPhase={genieSetup.setupPhase}
          setupMode={genieSetup.setupMode}
          analysisResult={genieSetup.analysisResult}
          isAnalyzing={genieSetup.isAnalyzing}
          error={genieSetup.error}
          isResuming={genieSetup.isResuming}
          messages={genieSetup.messages}
          handleConversationToggleWithWakeLock={handleConversationToggleWithWakeLock}
          analyzeImage={genieSetup.analyzeImage}
          resumeSetup={genieSetup.resumeSetup}
          getStatusMessage={genieSetup.getStatusMessage}
          setSetupPhase={genieSetup.setSetupPhase}
          setSetupMode={genieSetup.setSetupMode}
          getService={genieSetup.getService}
          dispatch={genieSetup.dispatch}
          voiceAgent={genieSetup.voiceAgent}
        />
      )}

      <IPhoneTapReminder show={tapReminder.showReminder} onDismiss={tapReminder.dismissReminder} />
    </div>
  );
};

export default GenieSetupPage;
